import {
  Bookmark,
  CircleDollarSign,
  Clock,
  Columns3,
  CreditCard,
  DoorOpen,
  Download,
  Globe,
  HeartPulse,
  Hourglass,
  Layers,
  type LucideIcon,
  Plus,
  Radar,
  SlidersHorizontal,
  Sprout,
  Star,
  Tag,
  Trash2,
  UserCheck,
  UserPlus,
  Users,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { Avatar, Chip, FilterChip, HealthBar, Monogram, PlanTag } from '@/components/redesign/bits'
import { NativeSelect } from '@/components/ui/native-select'
import { SearchField } from '@/components/ui/search-field'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState, ErrorState } from '@/components/ui/states'
import { PageTitle } from '@/app/layout'
import { errorMessage } from '@/lib/api/errors'
import type { CustomerSummary } from '@/lib/api/types'
import { cn } from '@/lib/cn'
import { formatDate, formatMoney, formatRelative, numberFmt } from '@/lib/format'
import { useNow } from '@/lib/use-now'
import {
  type CustomerFilters,
  exportCustomers,
  useBulkCustomers,
  useCustomers,
  useDeleteView,
  useSaveView,
  useSavedViews,
} from './api'

/** The side menu's ready-made segments (CustomerQuery::SEGMENTS on the server). */
const SEGMENTS: { key: string; label: string; icon?: LucideIcon; dot?: string; group?: string }[] = [
  { key: '', label: 'All customers', icon: Users },
  { key: 'paying', label: 'Paying', icon: CircleDollarSign },
  { key: 'free', label: 'Free', icon: Sprout },
  { key: 'new', label: 'Signed up this week', icon: UserPlus },
  { key: 'risk', label: 'At risk', dot: 'bad', group: 'Watch' },
  { key: 'payment_issue', label: 'Payment failing', dot: 'bad' },
  { key: 'ending', label: 'Set to end', dot: 'warn' },
  { key: 'closing', label: 'Closing when unused', dot: 'warn' },
  { key: 'duplicates', label: 'Possible duplicates', dot: 'warn' },
]

const SORTS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'mrr', label: 'Highest paying first' },
  { value: 'health', label: 'Least healthy first' },
  { value: 'last_active', label: 'Most recently active' },
  { value: 'revenue', label: 'Paid the most' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'name', label: 'Name, A to Z' },
]

/** Filters shown as chips; the rest sit under "More filters". */
const CHIP_KEYS = ['plan', 'market', 'channel', 'health', 'activity', 'manager', 'tag'] as const
const MORE_KEYS = ['billing', 'signed_up', 'marketing', 'tickets', 'unverified', 'internal'] as const

/** Columns that can be hidden; the choice stays in this browser. */
const COLUMNS = [
  { key: 'plan', label: 'Plan' },
  { key: 'mrr', label: 'A month' },
  { key: 'health', label: 'Health' },
  { key: 'websites', label: 'Websites' },
  { key: 'active', label: 'Last active' },
  { key: 'channel', label: 'Came from' },
  { key: 'paid', label: 'Paid in all' },
  { key: 'since', label: 'Customer since' },
  { key: 'manager', label: 'Manager' },
] as const
type ColumnKey = (typeof COLUMNS)[number]['key']
const DEFAULT_COLUMNS: ColumnKey[] = ['plan', 'mrr', 'health', 'websites', 'active', 'channel', 'manager']
const COLUMNS_KEY = 'arvaseo-cp-customer-columns'

function storedColumns(): ColumnKey[] {
  try {
    const raw = window.localStorage.getItem(COLUMNS_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    return Array.isArray(parsed)
      ? (parsed.filter((c) => COLUMNS.some((k) => k.key === c)) as ColumnKey[])
      : DEFAULT_COLUMNS
  } catch {
    return DEFAULT_COLUMNS
  }
}

const channelColor = (label: string | null | undefined) =>
  label === 'Google Ads'
    ? 'var(--viz-1)'
    : label === 'Meta ads'
      ? 'var(--viz-2)'
      : label
        ? 'var(--viz-4)'
        : 'var(--viz-other)'

/**
 * Customers (redesign of 2026-10-16): segments and saved views on the left, filter chips, the
 * summary line, and a table with health and where each customer came from. Filters live in the
 * address bar so a view can be shared. Opening, searching and exporting are recorded.
 */
export function CustomersPage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const filters: CustomerFilters = Object.fromEntries([...params.entries()].filter(([k]) => k !== 'page'))
  const page = Number(params.get('page') ?? '1') || 1
  const closed = filters.status === 'closed'
  const [draft, setDraft] = useState(filters.search ?? '')
  const [exporting, setExporting] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [columns, setColumns] = useState<ColumnKey[]>(storedColumns)
  const [panel, setPanel] = useState<'columns' | 'more' | 'tag' | 'manager' | 'save' | null>(null)
  const [text, setText] = useState('')
  const customers = useCustomers(filters, page)
  const views = useSavedViews()
  const saveView = useSaveView()
  const deleteView = useDeleteView()
  const bulk = useBulkCustomers()
  const meta = customers.data?.meta
  const rows = customers.data?.data ?? []
  const now = useNow()

  const set = (changes: Record<string, string | null>, keepPage = false) => {
    setSelected(new Set())
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(changes)) {
          if (v === null || v === '') next.delete(k)
          else next.set(k, v)
        }
        if (!keepPage) next.delete('page')
        return next
      },
      { replace: true },
    )
  }

  useEffect(() => {
    const t = setTimeout(() => {
      if ((filters.search ?? '') !== draft.trim()) set({ search: draft.trim() })
    }, 400)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the typed search drives this
  }, [draft])

  const toggleColumn = (key: ColumnKey) => {
    const next = columns.includes(key) ? columns.filter((c) => c !== key) : [...columns, key]
    setColumns(next)
    try {
      window.localStorage.setItem(COLUMNS_KEY, JSON.stringify(next))
    } catch {
      // Not kept in a private window; the change still applies until the page reloads.
    }
  }

  const download = async () => {
    setExporting(true)
    try {
      await exportCustomers(filters)
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setExporting(false)
    }
  }

  const applyView = (viewFilters: Record<string, string>) =>
    setParams(new URLSearchParams(Object.entries(viewFilters).filter(([, v]) => v)), { replace: true })
  const activeFilters = [...CHIP_KEYS, ...MORE_KEYS].filter((k) => (filters[k] ?? '') !== '')
  const show = (key: ColumnKey) => columns.includes(key)
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id))
  const runBulk = (input: { add_tag?: string; account_manager_id?: string | null }, done: string) =>
    bulk.mutate(
      { ids: [...selected], ...input },
      {
        onSuccess: () => {
          toast.success(done)
          setSelected(new Set())
          setPanel(null)
          setText('')
        },
        onError: (e) => toast.error(errorMessage(e)),
      },
    )

  const segmentCount = (key: string) => meta?.segments?.[key || 'all']

  return (
    <>
      <PageTitle
        title="Customers"
        description="Every workspace, what it pays, how healthy it is and where it came from. Internal accounts are left out unless you ask for them."
        actions={
          meta?.can_export ? (
            <button type="button" className="btn" disabled={exporting} onClick={() => void download()}>
              <Download aria-hidden="true" />
              Export CSV
            </button>
          ) : undefined
        }
      />
      <div className="cust">
        <nav className="segs" aria-label="Customer segments">
          {SEGMENTS.map((s) => (
            <div key={s.key || 'all'} style={{ display: 'contents' }}>
              {s.group && (
                <>
                  <hr />
                  <div className="pop-l">{s.group}</div>
                </>
              )}
              <button
                type="button"
                className={cn('sg', !closed && (filters.segment ?? '') === s.key && 'on')}
                onClick={() => set({ segment: s.key || null, status: null })}
              >
                {s.dot ? (
                  <span className={cn('dot', s.dot)} />
                ) : s.icon ? (
                  <s.icon aria-hidden="true" />
                ) : null}
                {s.label}
                {segmentCount(s.key) !== undefined && <span className="c">{segmentCount(s.key)}</span>}
              </button>
            </div>
          ))}
          <button
            type="button"
            className={cn('sg', closed && 'on')}
            onClick={() => set({ status: 'closed', segment: null })}
          >
            <DoorOpen aria-hidden="true" />
            Closed, deleting soon
          </button>
          <hr />
          <div className="pop-l">Saved views</div>
          <button
            type="button"
            className={cn('sg', filters.segment === 'mine' && 'on')}
            onClick={() => set({ segment: 'mine', status: null })}
          >
            <Star aria-hidden="true" />
            My accounts
            {segmentCount('mine') !== undefined && <span className="c">{segmentCount('mine')}</span>}
          </button>
          {(views.data ?? []).map((v) => (
            <span key={v.id} className="group relative flex">
              <button type="button" className="sg pr-8" onClick={() => applyView(v.filters)}>
                <Bookmark aria-hidden="true" />
                <span className="truncate">{v.name}</span>
              </button>
              <button
                type="button"
                className="absolute top-1/2 right-2 hidden size-6 -translate-y-1/2 place-items-center rounded text-ink-subtle group-hover:grid hover:bg-surface-sunken hover:text-ink"
                aria-label={`Remove the saved view ${v.name}`}
                onClick={() => deleteView.mutate(v.id, { onError: (e) => toast.error(errorMessage(e)) })}
              >
                <Trash2 className="size-3.5" aria-hidden="true" />
              </button>
            </span>
          ))}
          {panel === 'save' ? (
            <form
              className="grid gap-2 px-2.5 py-1"
              onSubmit={(e) => {
                e.preventDefault()
                if (!text.trim()) return
                saveView.mutate(
                  { name: text.trim(), filters: Object.fromEntries(params.entries()) },
                  {
                    onSuccess: () => {
                      toast.success('Saved. It’s in the side menu.')
                      setPanel(null)
                      setText('')
                    },
                    onError: (er) => toast.error(errorMessage(er)),
                  },
                )
              }}
            >
              <label htmlFor="view-name" className="text-xs font-semibold text-ink-muted">
                Name this view
              </label>
              <input
                id="view-name"
                className="inp"
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={60}
                autoFocus
              />
              <span className="acts">
                <button type="submit" className="btn sm primary" disabled={!text.trim()}>
                  Save
                </button>
                <button type="button" className="btn sm ghost" onClick={() => setPanel(null)}>
                  Cancel
                </button>
              </span>
            </form>
          ) : (
            <button type="button" className="sg add" onClick={() => setPanel('save')}>
              <Plus aria-hidden="true" />
              Save this view
            </button>
          )}
        </nav>

        <div className="sec">
          <div className="fbar">
            <SearchField
              label="Search customers"
              placeholder="Name, email or website"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <FilterChip
              label="Plan"
              icon={<Layers aria-hidden="true" />}
              value={filters.plan}
              onChange={(v) => set({ plan: v })}
              options={[...(meta?.plans ?? []), ...(meta?.lifetime_plans ?? [])].map((p) => ({
                value: p.key,
                label: p.name,
              }))}
            />
            <FilterChip
              label="Market"
              icon={<Globe aria-hidden="true" />}
              value={filters.market}
              onChange={(v) => set({ market: v })}
              options={[
                { value: 'tier1', label: 'Tier 1 (US UK CA AU IE NZ)' },
                { value: 'hubs', label: 'Hubs (PH ZA SG MY)' },
                { value: 'other', label: 'Everywhere else' },
              ]}
            />
            <FilterChip
              label="Came from"
              icon={<Radar aria-hidden="true" />}
              value={filters.channel}
              onChange={(v) => set({ channel: v })}
              options={[
                { value: 'google', label: 'Google Ads' },
                { value: 'meta', label: 'Meta ads' },
                { value: 'tagged', label: 'Another campaign link' },
                { value: 'untagged', label: 'No campaign tags' },
              ]}
            />
            <FilterChip
              label="Health"
              icon={<HeartPulse aria-hidden="true" />}
              value={filters.health}
              onChange={(v) => set({ health: v })}
              options={[
                { value: 'good', label: 'Healthy (70+)' },
                { value: 'mid', label: 'Watch (45–69)' },
                { value: 'low', label: 'At risk (under 45)' },
              ]}
            />
            <FilterChip
              label="Last active"
              icon={<Clock aria-hidden="true" />}
              value={filters.activity}
              onChange={(v) => set({ activity: v })}
              options={[
                { value: 'active_7', label: 'In the last 7 days' },
                { value: 'active_30', label: 'In the last 30 days' },
                { value: 'inactive_30', label: 'Not for 30 days or more' },
                { value: 'inactive_90', label: 'Not for 90 days or more' },
                { value: 'never', label: 'Never used the app' },
              ]}
            />
            <FilterChip
              label="Account manager"
              icon={<UserCheck aria-hidden="true" />}
              value={filters.manager}
              onChange={(v) => set({ manager: v })}
              options={[
                { value: 'me', label: 'Me' },
                { value: 'none', label: 'Nobody' },
                ...(meta?.managers ?? []).map((m) => ({ value: m.id, label: m.name })),
              ]}
            />
            {(meta?.tags.length ?? 0) > 0 && (
              <FilterChip
                label="Tag"
                icon={<Tag aria-hidden="true" />}
                value={filters.tag}
                onChange={(v) => set({ tag: v })}
                options={(meta?.tags ?? []).map((t) => ({ value: t, label: t }))}
              />
            )}
            <span className="anchor">
              <button
                type="button"
                className={cn('fchip', MORE_KEYS.some((k) => filters[k]) && 'on')}
                onClick={() => setPanel(panel === 'more' ? null : 'more')}
                aria-expanded={panel === 'more'}
              >
                <SlidersHorizontal aria-hidden="true" />
                More filters
              </button>
              {panel === 'more' && <MoreFilters filters={filters} set={set} onClose={() => setPanel(null)} />}
            </span>
            {activeFilters.length > 0 && (
              <button
                type="button"
                className="linkbtn"
                onClick={() => set(Object.fromEntries(activeFilters.map((k) => [k, null])))}
              >
                Clear filters
              </button>
            )}
          </div>

          <div className="bar" style={{ fontSize: 13, color: 'var(--muted)' }}>
            <span>
              <b style={{ color: 'var(--ink)' }}>{numberFmt.format(meta?.total ?? 0)}</b>{' '}
              {closed ? 'closed workspaces' : 'customers'}
              {meta?.summary && !closed && (
                <>
                  {' · '}
                  <b style={{ color: 'var(--ink)' }}>{formatMoney(meta.summary.mrr)}</b> a month
                  {meta.summary.average_health !== null && (
                    <>
                      {' · '}average health{' '}
                      <b style={{ color: 'var(--ink)' }}>{meta.summary.average_health}</b>
                    </>
                  )}
                </>
              )}
              {meta?.marketing && (
                <span className="text-ink-subtle">
                  {' · '}
                  {meta.marketing.opted_in} opted in to product news
                  {meta.marketing.reachable === meta.marketing.opted_in
                    ? ''
                    : ` (${meta.marketing.reachable} with a confirmed email)`}
                </span>
              )}
            </span>
            <span className="push">
              <NativeSelect
                pill
                aria-label="Sort customers"
                value={filters.sort ?? (closed ? '' : 'newest')}
                onChange={(e) => set({ sort: e.target.value === 'newest' ? null : e.target.value })}
              >
                {closed && <option value="">Closed most recently</option>}
                {SORTS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </NativeSelect>
            </span>
            <span className="anchor">
              <button
                type="button"
                className="btn sm ghost"
                onClick={() => setPanel(panel === 'columns' ? null : 'columns')}
                aria-expanded={panel === 'columns'}
              >
                <Columns3 aria-hidden="true" />
                Columns
              </button>
              {panel === 'columns' && (
                <div className="pop right" role="group" aria-label="Columns">
                  <div className="pop-l">Show columns</div>
                  {COLUMNS.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      className="opt"
                      role="checkbox"
                      aria-checked={show(c.key)}
                      onClick={() => toggleColumn(c.key)}
                    >
                      <span className={cn('cbx', show(c.key) && 'on')}>
                        <Check />
                      </span>
                      {c.label}
                    </button>
                  ))}
                </div>
              )}
            </span>
          </div>

          {selected.size > 0 && (
            <div className="bulk" role="region" aria-label="Selected customers">
              <span>{selected.size} selected</span>
              {panel === 'tag' ? (
                <form
                  className="acts"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (text.trim()) runBulk({ add_tag: text.trim() }, `Tag added to ${selected.size}.`)
                  }}
                >
                  <input
                    className="inp"
                    style={{ height: 32, width: 180 }}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="e.g. webinar, at risk"
                    aria-label="Tag"
                    autoFocus
                  />
                  <button type="submit" className="btn sm primary" disabled={!text.trim() || bulk.isPending}>
                    Add tag
                  </button>
                </form>
              ) : panel === 'manager' ? (
                <span className="acts">
                  <NativeSelect
                    pill
                    aria-label="Account manager"
                    defaultValue=""
                    onChange={(e) =>
                      runBulk(
                        { account_manager_id: e.target.value === 'none' ? null : e.target.value },
                        e.target.value === 'none' ? 'Account manager removed.' : 'Account manager set.',
                      )
                    }
                  >
                    <option value="" disabled>
                      Choose someone
                    </option>
                    {(meta?.managers ?? []).map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                    <option value="none">Nobody</option>
                  </NativeSelect>
                </span>
              ) : (
                <>
                  <button type="button" className="btn ghost" onClick={() => setPanel('tag')}>
                    <Tag aria-hidden="true" />
                    Add a tag
                  </button>
                  <button type="button" className="btn ghost" onClick={() => setPanel('manager')}>
                    <UserCheck aria-hidden="true" />
                    Assign a manager
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn ghost"
                style={{ marginLeft: 'auto' }}
                onClick={() => {
                  setSelected(new Set())
                  setPanel(null)
                }}
              >
                <X aria-hidden="true" />
                Clear
              </button>
            </div>
          )}

          <div className="tw">
            {customers.isPending ? (
              <Skeleton className="m-5 h-32" />
            ) : customers.isError ? (
              <ErrorState error={customers.error} onRetry={() => void customers.refetch()} />
            ) : rows.length === 0 ? (
              <EmptyState
                title={closed ? 'No closed workspaces' : 'No customers match'}
                description={
                  closed
                    ? 'Workspaces closed in the last 30 days show here until they’re deleted for good.'
                    : 'Clear a filter or pick another segment.'
                }
              />
            ) : (
              <table className="t" aria-label="Customers">
                <thead>
                  <tr>
                    <th style={{ width: 34 }}>
                      <button
                        type="button"
                        className={cn('cbx', allSelected && 'on')}
                        aria-label={
                          allSelected ? 'Clear the selection' : 'Select every customer on this page'
                        }
                        onClick={() => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)))}
                      >
                        <Check />
                      </button>
                    </th>
                    <th>Customer</th>
                    {show('plan') && <th>Plan</th>}
                    {show('mrr') && <th className="r">A month</th>}
                    {show('health') && <th>Health</th>}
                    {show('websites') && <th className="r">Sites</th>}
                    {show('active') && <th>Last active</th>}
                    {show('channel') && <th>Came from</th>}
                    {show('paid') && <th className="r">Paid in all</th>}
                    {show('since') && <th>Since</th>}
                    {show('manager') && <th>Manager</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <Row
                      key={c.id}
                      c={c}
                      now={now}
                      show={show}
                      checked={selected.has(c.id)}
                      onCheck={() =>
                        setSelected((s) => {
                          const next = new Set(s)
                          if (next.has(c.id)) next.delete(c.id)
                          else next.add(c.id)
                          return next
                        })
                      }
                      onOpen={() => void navigate(`/customers/${c.id}`)}
                    />
                  ))}
                </tbody>
              </table>
            )}
            {meta && rows.length > 0 && (
              <div className="t-foot">
                <span>
                  {meta.last_page > 1 ? `Page ${meta.current_page} of ${meta.last_page} · ` : ''}
                  {numberFmt.format(meta.total)} {meta.total === 1 ? 'customer' : 'customers'}
                </span>
                {meta.last_page > 1 ? (
                  <span className="acts">
                    <button
                      type="button"
                      className="btn sm ghost"
                      disabled={page <= 1}
                      onClick={() => set({ page: String(page - 1) }, true)}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="btn sm ghost"
                      disabled={page >= meta.last_page}
                      onClick={() => set({ page: String(page + 1) }, true)}
                    >
                      Next
                    </button>
                  </span>
                ) : (
                  <span>
                    Health mixes activity, feature use, billing and support. Open a customer to see the parts.
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

function Check() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

function MoreFilters({
  filters,
  set,
  onClose,
}: {
  filters: CustomerFilters
  set: (changes: Record<string, string | null>) => void
  onClose: () => void
}) {
  const select = (key: keyof CustomerFilters, label: string, options: { value: string; label: string }[]) => (
    <label className="grid gap-1 text-xs font-semibold text-ink-muted">
      {label}
      <NativeSelect value={filters[key] ?? ''} onChange={(e) => set({ [key]: e.target.value })}>
        <option value="">Any</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </NativeSelect>
    </label>
  )
  const check = (key: keyof CustomerFilters, on: string, label: string) => (
    <label className="flex items-center gap-2 text-[13px] text-ink">
      <input
        type="checkbox"
        className="size-4"
        checked={filters[key] === on}
        onChange={(e) => set({ [key]: e.target.checked ? on : null })}
      />
      {label}
    </label>
  )
  return (
    <div className="pop grid gap-3 p-4" style={{ minWidth: 280 }} role="group" aria-label="More filters">
      {select('billing', 'Billing', [
        { value: 'paying', label: 'Paying (subscriptions and lifetime plans)' },
        { value: 'lifetime', label: 'Lifetime plan' },
        { value: 'complimentary', label: 'Complimentary' },
        { value: 'payment_issue', label: 'Payment failed' },
        { value: 'ending', label: 'Cancelled, still running' },
        { value: 'lapsed', label: 'Paid before, not now' },
        { value: 'never_paid', label: 'Never paid' },
      ])}
      {select('signed_up', 'Signed up', [
        { value: '7', label: 'In the last 7 days' },
        { value: '30', label: 'In the last 30 days' },
        { value: '90', label: 'In the last 90 days' },
        { value: '365', label: 'In the last 12 months' },
      ])}
      {select('marketing', 'Product news', [
        { value: 'opted_in', label: 'Opted in' },
        { value: 'opted_out', label: 'Not opted in' },
      ])}
      {check('tickets', 'open', 'Has open tickets')}
      {check('unverified', '1', 'Owner hasn’t verified their email')}
      {check('internal', '1', 'Show internal accounts')}
      <button type="button" className="btn sm" onClick={onClose}>
        Done
      </button>
    </div>
  )
}

function Row({
  c,
  now,
  show,
  checked,
  onCheck,
  onOpen,
}: {
  c: CustomerSummary
  now: number
  show: (key: ColumnKey) => boolean
  checked: boolean
  onCheck: () => void
  onOpen: () => void
}) {
  const days = c.last_active_at ? (now - Date.parse(c.last_active_at)) / 86400000 : null
  return (
    <tr className={cn('click', checked && 'sel')} onClick={onOpen}>
      <td>
        <button
          type="button"
          className={cn('cbx', checked && 'on')}
          aria-label={`Select ${c.name}`}
          aria-pressed={checked}
          onClick={(e) => {
            e.stopPropagation()
            onCheck()
          }}
        >
          <Check />
        </button>
      </td>
      <td>
        <div className="who">
          <Monogram name={c.name} size="sm" />
          <div>
            <b>
              <Link to={`/customers/${c.id}`} onClick={(e) => e.stopPropagation()}>
                {c.name}
              </Link>{' '}
              {c.payment_issue ? (
                <Chip tone="bad" className="h-5 text-[11px]">
                  <CreditCard aria-hidden="true" />
                  Payment failing
                </Chip>
              ) : c.ending ? (
                <Chip tone="warn" className="h-5 text-[11px]">
                  Set to end
                </Chip>
              ) : c.unused_reminders ? (
                <Chip tone="warn" className="h-5 text-[11px]">
                  <Hourglass aria-hidden="true" />
                  Reminder {c.unused_reminders} of 3
                </Chip>
              ) : null}
              {c.free_limited ? (
                <Chip tone="warn" className="h-5 text-[11px]">
                  Free limited
                </Chip>
              ) : (
                c.possible_duplicate && (
                  <Chip tone="warn" className="h-5 text-[11px]">
                    Possible duplicate
                  </Chip>
                )
              )}
              {c.internal_account && <Chip className="h-5 text-[11px]">Internal</Chip>}
              {c.complimentary && (
                <Chip tone="lime" className="h-5 text-[11px]">
                  Complimentary
                </Chip>
              )}
            </b>
            <small>
              {[c.owner?.name, c.country, c.domain ?? c.owner?.email].filter(Boolean).join(' · ')}
              {c.deletes_on && (
                <span className="text-danger"> · deleted for good on {formatDate(c.deletes_on)}</span>
              )}
            </small>
          </div>
        </div>
      </td>
      {show('plan') && (
        <td>
          <PlanTag plan={c.plan.key} name={c.plan.name} yearly={c.interval === 'annual'} />
        </td>
      )}
      {show('mrr') && (
        <td className="r tabular">{c.mrr ? formatMoney(c.mrr) : <span className="m">—</span>}</td>
      )}
      {show('health') && <td>{c.health !== undefined ? <HealthBar value={c.health} /> : '—'}</td>}
      {show('websites') && <td className="r tabular">{c.websites}</td>}
      {show('active') && (
        <td
          className={days === null || days > 30 ? '' : 'm'}
          style={days === null || days > 30 ? { color: 'var(--warn)', fontWeight: 600 } : undefined}
        >
          {c.last_active_at ? formatRelative(c.last_active_at) : 'Never'}
        </td>
      )}
      {show('channel') && (
        <td style={{ whiteSpace: 'nowrap' }} title={c.campaign ? `Campaign: ${c.campaign}` : undefined}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <i
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                background: channelColor(c.channel),
                display: 'block',
              }}
            />
            {c.channel ?? 'No campaign tags'}
          </span>
        </td>
      )}
      {show('paid') && (
        <td className="r tabular">{c.revenue ? formatMoney(c.revenue) : <span className="m">—</span>}</td>
      )}
      {show('since') && <td className="m">{formatDate(c.created_at ?? '')}</td>}
      {show('manager') && (
        <td>
          <Avatar name={c.account_manager?.name} size="xs" />
        </td>
      )}
    </tr>
  )
}
